/**
 * Miata Registry
 * Copyright (C) 2024-2026 Matthew Congrove
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { Link } from 'react-router-dom';
import Symbol from '../assets/symbol.svg?react';
import { AGPL_LICENSE_URL, GITHUB_REPO_URL } from '../constants/repo';
import { useModal } from '../context/ModalContext';
import { Icon } from './Icon';
import { SitePulse } from './SitePulse';

export const Footer = () => {
	const { openModal } = useModal();

	return (
		<footer className="bg-brg-dark py-8 lg:pt-16">
			<div className="container mx-auto">
				<div className="flex flex-col lg:flex-row gap-8 lg:gap-24 mb-8 lg:mb-16">
					<div className="lg:w-1/4">
						<div className="flex flex-col gap-1.5 w-32 mb-6 text-brg-mid">
							<Symbol className="w-full h-auto" />

							<small className="w-full text-center uppercase text-xs font-medium">
								The Miata Registry
							</small>
						</div>

						<p className="text-sm lg:text-base text-brg-mid mb-6">
							A community-driven project documenting the history
							of limited edition Mazda Miatas.
						</p>

						<div className="flex gap-4">
							<a
								href="https://www.instagram.com/miataregistry/"
								className="text-brg-mid hover:text-brg-light transition-colors"
								target="_blank"
								rel="noopener noreferrer"
							>
								<Icon name="instagram" className="text-3xl" />

								<span className="sr-only">Instagram</span>
							</a>

							<a
								href={GITHUB_REPO_URL}
								className="text-brg-mid hover:text-brg-light transition-colors"
								target="_blank"
								rel="noopener noreferrer"
							>
								<Icon name="github" className="text-3xl" />

								<span className="sr-only">GitHub</span>
							</a>
						</div>
					</div>

					<div className="flex gap-8 lg:gap-16 text-sm lg:text-base">
						<div>
							<h3 className="font-medium text-white mb-4">
								Registry
							</h3>

							<ul className="space-y-2">
								<li>
									<Link
										to="/registry"
										className="text-brg-mid hover:text-brg-light transition-colors"
										data-cy="footer-browse-the-cars"
									>
										Browse Cars
									</Link>
								</li>
								<li>
									<Link
										to="/registry/editions"
										className="text-brg-mid hover:text-brg-light transition-colors"
									>
										Browse Editions
									</Link>
								</li>
								<li>
									<span
										onClick={() => openModal('register')}
										className="text-brg-mid hover:text-brg-light transition-colors cursor-pointer"
										data-cy="footer-register-your-miata"
									>
										Register your Miata
									</span>
								</li>
								<li>
									<Link
										to="/about#statistics"
										className="text-brg-mid hover:text-brg-light transition-colors"
									>
										Statistics
									</Link>
								</li>
							</ul>
						</div>

						<div>
							<h3 className="font-medium text-white mb-4">
								About
							</h3>

							<ul className="space-y-2">
								<li>
									<Link
										to="/about"
										className="text-brg-mid hover:text-brg-light transition-colors"
									>
										About
									</Link>
								</li>
								<li>
									<Link
										to="/rarity"
										className="flex items-center gap-1.5 text-brg-mid hover:text-brg-light transition-colors"
									>
										Rarity Scores{' '}
										<Icon
											name="crown"
											className="text-sm text-yellow-300/50"
										/>
									</Link>
								</li>
								<li>
									<Link
										to="/news"
										className="text-brg-mid hover:text-brg-light transition-colors"
									>
										News
									</Link>
								</li>
								<li>
									<Link
										to="/resources"
										className="text-brg-mid hover:text-brg-light transition-colors"
									>
										Resources
									</Link>
								</li>
								<li>
									<Link
										to="/about#contact"
										className="text-brg-mid hover:text-brg-light transition-colors"
									>
										Contact
									</Link>
								</li>
							</ul>
						</div>
					</div>
				</div>

				<div className="flex flex-col items-center gap-8 border-t border-white/10 pt-8 text-xs lg:text-sm">
					<div className="flex flex-col items-center gap-1.5">
						<p className="text-center text-brg-mid">
							© {new Date().getFullYear()}{' '}
							<a
								href="https://mattcongrove.com"
								target="_blank"
								rel="noopener noreferrer"
								className="text-brg-mid underline whitespace-nowrap"
							>
								Matthew Congrove
							</a>
							.{' '}
							<a
								href={GITHUB_REPO_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="text-brg-mid underline whitespace-nowrap"
							>
								Source code
							</a>{' '}
							and{' '}
							<a
								href="https://archive.org/details/@miataregistry"
								target="_blank"
								rel="noopener noreferrer"
								className="text-brg-mid underline whitespace-nowrap"
							>
								data
							</a>{' '}
							licensed under{' '}
							<a
								href={AGPL_LICENSE_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="text-brg-mid underline whitespace-nowrap"
							>
								AGPL-3
							</a>
							. All other rights reserved. You can reach us at{' '}
							<a
								href="mailto:support@miataregistry.com"
								className="text-brg-mid underline whitespace-nowrap"
							>
								support@miataregistry.com
							</a>
							.
						</p>

						<p className="text-center text-brg-mid">
							<Link
								to="/legal"
								className="text-brg-mid underline whitespace-nowrap"
							>
								Privacy Policy and Terms
							</Link>
						</p>
					</div>

					<SitePulse />
				</div>
			</div>
		</footer>
	);
};
